import { useParams } from 'react-router'
import { Suspense, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui'
import { usePageContext } from '@/lib/context/page'
import {
  useGetCogsDirectCostsCategories,
  useGetDirectCostsPeriodicData,
  useSuspenseUnitsSettings
} from '@/feature/financial-model/ecommerce/cogs/api'
import { PageTabs } from '@/components/shared/page-sections.tsx'
import { TabsContent } from '@/components/ui/tabs.tsx'
import { Accordion } from '@/components/ui/accordion.tsx'
import { NavigationProvider } from '@/lib/context/navigation'
import { ForecastDataTable, HistoricalDataTable } from '@/feature/financial-model/ecommerce/cogs/components/direct-costs-tables.tsx'
import { Show } from '@/components/shared/show.tsx'
import { Skeleton } from '@/components/ui/skeleton'

const CategoryTables = ({ categoryId }: { categoryId: string }) => {
  const { state } = usePageContext()
  const { id: financialModelId } = useParams<{ id: string }>()
  const { data: categoryUnitSettings } = useSuspenseUnitsSettings(financialModelId!, categoryId)
  const { data: periodicData } = useGetDirectCostsPeriodicData({
    financialModelId: financialModelId!,
    period_type: state.queryPeriod,
    view: state.view,
    categoryId
  })

  const { t } = useTranslation(`cogs`, {
    keyPrefix: `cogs.periods.direct_costs.alert`
  })

  if (periodicData.length === 0) {
    return (
      <Alert variant='info' className='mt-4'>
        <AlertTitle className='text-lg font-medium'>{t(`title`, { period: state.period })}</AlertTitle>
        <AlertDescription className='text-sm'>{t(`description`)}</AlertDescription>
      </Alert>
    )
  }

  if (categoryUnitSettings.level_of_granularity === `by_product`) {
    return (
      <div>
        <Accordion type='multiple' defaultValue={periodicData.map(periods => periods?.periods[0]?.product_id!)}>
          {periodicData
            .sort((a, b) => a.periods[0].product_name?.localeCompare(b.periods[0].product_name ?? ``) ?? 0)
            .map(periods => (
              <NavigationProvider key={periods?.periods[0]?.product_id}>
                <Show
                  when={state.period === `historical`}
                  fallback={
                    <ForecastDataTable periods={periods?.periods ?? []} config={periods?.config} categoryId={categoryId} variant={`accordion-item`} />
                  }
                >
                  <HistoricalDataTable periods={periods?.periods ?? []} categoryId={categoryId} variant={`accordion-item`} />
                </Show>
              </NavigationProvider>
            ))}
        </Accordion>
      </div>
    )
  }

  return (
    <div>
      <Accordion type='multiple' defaultValue={[String(periodicData[0].periods[0].product_id)]}>
        <NavigationProvider>
          <Show
            when={state.period === `historical`}
            fallback={
              <ForecastDataTable
                variant={`accordion-item`}
                periods={periodicData[0].periods}
                config={periodicData[0].config}
                categoryId={categoryId}
              />
            }
          >
            <HistoricalDataTable periods={periodicData[0].periods} variant={`accordion-item`} categoryId={categoryId} />
          </Show>
        </NavigationProvider>
      </Accordion>
    </div>
  )
}

export const DirectCostsDetailedTables = () => {
  const { id: financialModelId } = useParams<{ id: string }>()

  const {
    data: { has_categories, categories }
  } = useGetCogsDirectCostsCategories(financialModelId!)

  const triggers = useMemo(
    () =>
      categories
        .filter(category => Boolean(category))
        .map(category => ({
          key: category.id,
          value: category.id,
          children: category.name
        })),
    [categories]
  )

  if (!has_categories) {
    return <div>No categories, add in setup</div>
  }
  return (
    <PageTabs
      variant={`transparent`}
      tabListContainerClassName={`overflow-x-auto border-b border-gray-200 px-0 pt-0`}
      triggers={triggers}
      defaultValue={triggers[0].value}
    >
      {categories
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(category => (
          <TabsContent value={category.id} key={category.id}>
            <Suspense fallback={<Skeleton className='h-[500px] w-full' />}>
              <CategoryTables categoryId={category.id} />
            </Suspense>
          </TabsContent>
        ))}
    </PageTabs>
  )
}
